/**
 * Somas recorrentes da proposta, num lugar só.
 *
 * ## Por que este módulo existe
 *
 * O total mensal da tabela era recomposto à mão em quatro pontos — duas vezes em `investimento.ts`,
 * uma em `formal.ts` e uma na composição do consumo —, cada um com a mesma cadeia de `somarValores`.
 * Enquanto a proposta tinha só backup, a duplicação era inofensiva. Quando a assinatura SafeStorage
 * entrou no catálogo, todos os quatro tiveram de ser editados juntos, e esquecer um teria produzido o
 * defeito mais difícil de perceber que este documento admite: **a tabela não fecha**. O cliente soma
 * as linhas, chega a um número, e o total impresso é outro.
 *
 * O produto "Custos Adicionais" seria a terceira vez que a mesma cadeia precisaria crescer nos mesmos
 * quatro lugares. Em vez disso, a soma passa a ter um nome e um dono: acrescentar um produto ao
 * catálogo agora é editar uma linha aqui.
 *
 * As parcelas são somadas **já arredondadas**, e não convertidas depois de somar. É o que garante que
 * as partes exibidas fechem no total exibido: cada parcela já é exata ao centavo.
 *
 * ## Por que mora no domínio, e não no template
 *
 * Nasceu em `template-proposta`, porque o primeiro consumidor era a tabela de investimento. Mudou de
 * casa quando o dashboard passou a precisar do mesmo total: não é matemática de renderização, é preço.
 * Deixá-la no template obrigaria `services/api` a importar um cálculo financeiro do pacote que gera
 * HTML — e a próxima pessoa a duplicar a soma em vez de procurá-la lá.
 */

import { somarValores } from './dinheiro.ts';
import type { Resultados } from './documento.ts';
import type { Valor } from './tipos.ts';

/**
 * Produtos eMaster que compõem a mensalidade: backup, SafeStorage e custos adicionais.
 *
 * O que os une é a forma de precificação, não a natureza: os três têm preço de venda próprio, não
 * recebem margem e são abatidos pelo crédito AWS. É por isso que a proposta os apresenta sob o mesmo
 * rótulo, separados da infraestrutura.
 */
export function totalProdutosEmaster(r: Resultados): Valor {
  return somarValores(somarValores(r.licencasBackup, r.plataformaSafeStorage), r.custosAdicionais);
}

/**
 * Total mensal recorrente: a base mensal mais os produtos eMaster.
 *
 * É o número que fecha a tabela de investimento e o que a primeira linha do resumo financeiro chama de
 * "Consumo total previsto" — o mesmo valor com dois rótulos, de propósito, e o resumo declara que são
 * o mesmo para quem confere não desconfiar.
 *
 * **Não** inclui o deploy, que é pagamento único, nem desconta a redução do crédito. É o valor antes
 * de qualquer incentivo.
 */
export function totalMensalRecorrente(r: Resultados): Valor {
  return somarValores(r.baseMensal, totalProdutosEmaster(r));
}
